import { useEffect, useState } from 'react';

import {
  signUserMedia,
  type AdminChatAttachment,
  type AdminChatMessage,
  type AdminChatRow,
} from '@/lib/adminApi';
import { useUserChat, useUserChats } from '@/lib/useAdminUsers';
import { useWorkflowChat } from '@/lib/workflowsApi';

import { MediaPreviewDialog } from './MediaPreviewDialog';

function formatDate(value: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleString();
}

/**
 * The Chats tab of a user's card (ClickUp 869f4c8wg) — SUPERADMIN ONLY. The
 * page renders this tab only for `canReadUserChats(role)`; the API refuses
 * every other role with 403 and writes a strict audit receipt for every
 * read, so opening a chat here is an act the audit log shows.
 *
 * Two panes: the chat list (newest first, cursor-paged) and the transcript
 * of the selected chat (oldest first, the way a conversation reads). A tool
 * call is shown by NAME only — the API never sends its input or result.
 */
export function UserChatsTab({ userId }: { userId: string }) {
  const [cursorStack, setCursorStack] = useState<(string | null)[]>([null]);
  const cursor = cursorStack[cursorStack.length - 1] ?? null;
  const { data, isLoading, isError } = useUserChats(userId, cursor, true);
  const [selected, setSelected] = useState<string | null>(null);

  const rows = data?.rows ?? [];

  if (isLoading) return <p className="text-body text-fg-hint">Loading…</p>;
  if (isError) {
    return (
      <p className="text-body text-destructive">
        Could not load chats. This tab is for the superadmin role only.
      </p>
    );
  }
  if (rows.length === 0 && cursorStack.length <= 1) {
    return <p className="text-body text-fg-hint">No chats yet.</p>;
  }

  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
      <div>
        <div className="overflow-x-auto rounded-card border border-border">
          <table className="w-full text-left text-body" data-testid="user-chats-table">
            <thead>
              <tr className="border-b border-border bg-surface-muted text-meta text-fg-secondary">
                <th className="px-3 py-2">Title</th>
                <th className="px-3 py-2">Workspace</th>
                <th className="px-3 py-2">Messages</th>
                <th className="px-3 py-2">Started</th>
                <th className="px-3 py-2">Last activity</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((chat) => (
                <ChatListRow
                  key={chat.id}
                  chat={chat}
                  selected={chat.id === selected}
                  onSelect={() => setSelected(chat.id)}
                />
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-3 flex gap-2">
          <button
            type="button"
            disabled={cursorStack.length <= 1}
            onClick={() => setCursorStack((s) => s.slice(0, -1))}
            className="rounded-card border border-border px-3 py-1 text-meta disabled:cursor-not-allowed disabled:opacity-50"
          >
            Previous
          </button>
          <button
            type="button"
            disabled={!data?.next}
            onClick={() => setCursorStack((s) => [...s, data?.next ?? null])}
            className="rounded-card border border-border px-3 py-1 text-meta disabled:cursor-not-allowed disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>
      <div className="min-w-0">
        {selected ? (
          <ChatTranscript userId={userId} chatId={selected} />
        ) : (
          <p className="text-body text-fg-hint">Select a chat to read it.</p>
        )}
      </div>
    </div>
  );
}

function ChatListRow({
  chat,
  selected,
  onSelect,
}: {
  chat: AdminChatRow;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <tr
      className={`border-b border-border last:border-0 ${selected ? 'bg-state-selected' : ''}`}
      data-testid="user-chat-row"
    >
      <td className="px-3 py-2">
        <button
          type="button"
          onClick={onSelect}
          className="text-left font-medium text-primary hover:underline"
          aria-pressed={selected}
        >
          {chat.title ?? 'Untitled chat'}
        </button>
        {chat.complimentary_reason && (
          <span className="ml-2 rounded-pill bg-surface-muted px-2 py-0.5 text-micro text-fg-secondary">
            {chat.complimentary_reason}
          </span>
        )}
      </td>
      <td className="px-3 py-2 text-fg-secondary">{chat.workspace_name ?? chat.workspace_id ?? '—'}</td>
      <td className="px-3 py-2 text-fg-secondary">
        {chat.message_count}
        <span className="text-fg-hint"> ({chat.user_message_count} from user)</span>
      </td>
      <td className="px-3 py-2 text-fg-secondary">{formatDate(chat.date_created)}</td>
      <td className="px-3 py-2 text-fg-secondary">{formatDate(chat.date_updated)}</td>
    </tr>
  );
}

/**
 * `workflowId` reads the chat through the workflow-scoped endpoint (the User
 * workflows viewer); `userId` is then the workflow's owner, still used to sign
 * attachments.
 */
export function ChatTranscript({
  userId,
  chatId,
  workflowId,
}: {
  userId: string;
  chatId: string;
  workflowId?: string;
}) {
  const [cursorStack, setCursorStack] = useState<(string | null)[]>([null]);
  const cursor = cursorStack[cursorStack.length - 1] ?? null;
  const userChat = useUserChat(workflowId ? undefined : userId, chatId, cursor);
  const workflowChat = useWorkflowChat(workflowId, chatId, cursor);
  const { data, isLoading, isError } = workflowId ? workflowChat : userChat;
  const [signedUrls, setSignedUrls] = useState<Record<string, string | null>>({});
  const [preview, setPreview] = useState<AdminChatAttachment | null>(null);

  // A new chat starts at its first page.
  useEffect(() => {
    setCursorStack([null]);
  }, [chatId]);

  const rows = data?.rows ?? [];

  useEffect(() => {
    const keys = rows.flatMap((m) => m.attachments.map((a) => a.key));
    const unresolved = keys.filter((k) => !(k in signedUrls));
    if (unresolved.length === 0) return;
    // A system template has no owner to sign for: files stay unavailable.
    if (!userId) {
      setSignedUrls((prev) => ({
        ...prev,
        ...Object.fromEntries(unresolved.map((k) => [k, null])),
      }));
      return;
    }
    let cancelled = false;
    void signUserMedia(userId, unresolved)
      .then((urls) => {
        if (cancelled) return;
        setSignedUrls((prev) => ({ ...prev, ...urls }));
      })
      .catch(() => {
        if (cancelled) return;
        setSignedUrls((prev) => ({
          ...prev,
          ...Object.fromEntries(unresolved.map((k) => [k, null])),
        }));
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the row set, not signedUrls itself
  }, [userId, rows]);

  if (isLoading) return <p className="text-body text-fg-hint">Loading transcript…</p>;
  if (isError || !data) {
    return <p className="text-body text-destructive">Could not load this chat.</p>;
  }

  return (
    <div className="rounded-card border border-border" data-testid="chat-transcript">
      <div className="border-b border-border bg-surface-muted px-3 py-2">
        <p className="text-body font-medium text-fg-primary">{data.chat.title ?? 'Untitled chat'}</p>
        <p className="text-micro text-fg-hint">
          {data.chat.message_count} messages · started {formatDate(data.chat.date_created)}
        </p>
      </div>
      <ol className="divide-y divide-border">
        {rows.map((message) => (
          <TranscriptLine
            key={message.id}
            message={message}
            signedUrls={signedUrls}
            onOpenAttachment={setPreview}
          />
        ))}
      </ol>
      {(cursorStack.length > 1 || data.next) && (
        <div className="flex gap-2 border-t border-border px-3 py-2">
          <button
            type="button"
            disabled={cursorStack.length <= 1}
            onClick={() => setCursorStack((s) => s.slice(0, -1))}
            className="rounded-card border border-border px-3 py-1 text-meta disabled:cursor-not-allowed disabled:opacity-50"
          >
            Earlier
          </button>
          <button
            type="button"
            disabled={!data.next}
            onClick={() => setCursorStack((s) => [...s, data.next ?? null])}
            className="rounded-card border border-border px-3 py-1 text-meta disabled:cursor-not-allowed disabled:opacity-50"
          >
            Later
          </button>
        </div>
      )}
      {preview && (
        <MediaPreviewDialog
          mediaKey={preview.key}
          url={signedUrls[preview.key] ?? null}
          onClose={() => setPreview(null)}
        />
      )}
    </div>
  );
}

const ROLE_LABEL: Record<string, string> = {
  user: 'User',
  assistant: 'Assistant',
  tool: 'Tool',
  snapshot: 'System',
  reference: 'System',
  pending: 'System',
};

function TranscriptLine({
  message,
  signedUrls,
  onOpenAttachment,
}: {
  message: AdminChatMessage;
  signedUrls: Record<string, string | null>;
  onOpenAttachment: (attachment: AdminChatAttachment) => void;
}) {
  const isUser = message.role === 'user';
  return (
    <li className="px-3 py-2" data-testid={`chat-line-${message.role}`}>
      <div className="flex items-baseline justify-between gap-3">
        <span
          className={`text-micro font-semibold uppercase ${isUser ? 'text-primary' : 'text-fg-secondary'}`}
        >
          {ROLE_LABEL[message.role] ?? message.role}
        </span>
        <span className="text-micro text-fg-hint">{formatDate(message.date_created)}</span>
      </div>
      {message.text && (
        <p className="mt-1 whitespace-pre-wrap break-words text-body text-fg-primary">{message.text}</p>
      )}
      {message.summary && <p className="mt-1 text-meta italic text-fg-hint">{message.summary}</p>}
      {message.actions.length > 0 && (
        <ul className="mt-1 flex flex-wrap gap-1" aria-label="Tool calls">
          {message.actions.map((action, index) => (
            <li
              key={action.id ?? index}
              className="rounded-pill border border-border bg-surface-muted px-2 py-0.5 text-micro text-fg-secondary"
              title={action.has_result ? 'Completed' : 'No result recorded'}
            >
              {action.name ?? 'tool'}
              {action.has_result ? '' : ' (pending)'}
            </li>
          ))}
        </ul>
      )}
      {message.attachments.length > 0 && (
        <ul className="mt-1 flex flex-wrap gap-2" aria-label="Attachments">
          {message.attachments.map((attachment) => {
            const url = signedUrls[attachment.key];
            return (
              <li key={attachment.key}>
                <button
                  type="button"
                  onClick={() => onOpenAttachment(attachment)}
                  disabled={url === undefined}
                  className="rounded-card border border-border px-2 py-1 text-meta text-primary hover:underline disabled:cursor-wait disabled:opacity-50"
                >
                  {attachment.name ?? attachment.key.split('/').pop()}
                  {attachment.mime ? ` · ${attachment.mime}` : ''}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </li>
  );
}
